import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, HostListener } from '@angular/core';

@Component({
  selector: 'app-file-upload',
  standalone: false,
  template: `
    <div class="file-upload-dropzone"
         [class.dragover]="isDragOver"
         (click)="fileInput.click()">
      <input #fileInput type="file"
             [accept]="accept"
             [multiple]="multiple"
             style="display:none;"
             (change)="onFileSelected($event)">
      <button class="btn btn-secondary" type="button" (click)="$event.stopPropagation(); fileInput.click()">
        @if (chooseIcon) {
          <i [class]="chooseIcon"></i>
        }
        {{ chooseLabel || 'Datei auswählen' }}
      </button>
      <span class="file-upload-hint">oder Dateien hierher ziehen</span>
    </div>
  `,
  styles: [`
    .file-upload-dropzone {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem 1rem;
      border: 2px dashed #d1d5db;
      border-radius: 8px;
      background: #f9fafb;
      cursor: pointer;
      transition: border-color 0.15s, background-color 0.15s;
    }
    .file-upload-dropzone:hover {
      border-color: var(--color-brand, #6b7280);
      background: #f3f4f6;
    }
    .file-upload-dropzone.dragover {
      border-color: var(--color-brand, #f97316);
      background: #fff7ed;
    }
    .file-upload-hint {
      color: #6b7280;
      font-size: 0.875rem;
      pointer-events: none;
    }
  `]
})
export class FileUploadComponent {
  @Input() accept = '';
  @Input() multiple = false;
  @Input() auto = false;
  @Input() chooseLabel = '';
  @Input() chooseIcon = '';
  @Input() mode = 'basic';

  @Output() uploadHandler = new EventEmitter<{ files: File[] }>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  isDragOver = false;

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    this.isDragOver = true;
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const dropped = event.dataTransfer?.files;
    if (!dropped || dropped.length === 0) {
      return;
    }
    let files = Array.from(dropped);
    if (!this.multiple) {
      files = files.slice(0, 1);
    }
    if (this.accept) {
      files = files.filter(f => this.matchesAccept(f));
    }
    if (files.length > 0) {
      this.uploadHandler.emit({ files });
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    if (files.length > 0) {
      this.uploadHandler.emit({ files });
    }
    input.value = '';
  }

  private matchesAccept(file: File): boolean {
    const patterns = this.accept.split(',').map(s => s.trim()).filter(Boolean);
    if (patterns.length === 0) return true;
    return patterns.some(pattern => {
      if (pattern.startsWith('.')) {
        return file.name.toLowerCase().endsWith(pattern.toLowerCase());
      }
      if (pattern.endsWith('/*')) {
        const prefix = pattern.slice(0, pattern.length - 1);
        return file.type.startsWith(prefix);
      }
      return file.type === pattern;
    });
  }
}
